/**
 * Per-device "Keep me logged in" preference.
 *
 * Stored in localStorage, scoped to the user's internal UUID. Holds an
 * absolute expiry timestamp so an abandoned device is forced back to
 * the default idle timer after REMEMBER_ME_DAYS regardless of activity.
 *
 * Not synced across devices by design — a tick on a laptop must not
 * silently extend a session on a library computer.
 */

export const REMEMBER_ME_DAYS = 30;
const STORAGE_PREFIX = 'bower:remember-me:';

interface RememberMeEntry {
  readonly enabled: true;
  readonly expiresAt: number;
}

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}${userId}`;
}

export function setRememberMe(userId: string): void {
  const expiresAt = Date.now() + REMEMBER_ME_DAYS * 24 * 60 * 60 * 1000;
  const entry: RememberMeEntry = { enabled: true, expiresAt };
  localStorage.setItem(storageKey(userId), JSON.stringify(entry));
}

export function clearRememberMe(userId: string): void {
  localStorage.removeItem(storageKey(userId));
}

/**
 * Remove all rememberMe entries from localStorage, regardless of user.
 *
 * Called on sign-in so an entry written by a previous user on this
 * browser doesn't linger past the new user's session.
 */
export function clearAllRememberMe(): void {
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (key && key.startsWith(STORAGE_PREFIX)) {
      keys.push(key);
    }
  }
  for (const key of keys) {
    localStorage.removeItem(key);
  }
}

export function getRememberMe(userId: string): RememberMeEntry | null {
  const raw = localStorage.getItem(storageKey(userId));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as RememberMeEntry;
    if (parsed.enabled !== true || typeof parsed.expiresAt !== 'number') {
      return null;
    }
    if (parsed.expiresAt <= Date.now()) {
      localStorage.removeItem(storageKey(userId));
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}
